// StatusDotLabel — read-only "dot + label" status display for building
// and room cards in the redesigned capture surface.
//
// Mirrors the chip variant of `BuildingStatusBadge` semantically (same
// 3-state vocabulary, same accessible-name contract) but renders as a
// quieter inline marker that fits the minimal-list card style chosen
// for the field UI. The dot's colour and surrounding ring carry the
// status; the label text always accompanies it so the component never
// relies on colour alone (AGENTS.md invariant #10 / a11y note in the
// design handoff).
//
// This is intentionally stateless and non-interactive — the chip on
// `BuildingStatusBadge` keeps owning the toggle dialog where editing
// is allowed (e.g. inside `BuildingCaptureScreen`'s hero).

import type { ReactElement } from 'react'

import type { BuildingStatus } from '@citycare/shared'

interface DotPresentation {
  label: string
  // The coloured dot itself.
  dotClass: string
  // The text that follows the dot.
  textClass: string
}

const PRESENTATION: Record<BuildingStatus, DotPresentation> = {
  not_started: {
    label: 'Not started',
    dotClass: 'bg-[color:var(--color-cc-charcoal)]/40',
    textClass: 'text-[color:var(--color-cc-charcoal)]/60',
  },
  in_progress: {
    label: 'In progress',
    // Lime dot with a soft tinted ring picks up the design's
    // `box-shadow: 0 0 0 3px rgba(190,222,24,.20)` accent.
    dotClass:
      'bg-[color:var(--color-cc-lime)] ring-[3px] ring-[color:var(--color-cc-lime)]/30',
    textClass: 'text-[color:var(--color-cc-forest)]',
  },
  complete: {
    label: 'Completed',
    dotClass: 'bg-[color:var(--color-cc-forest)]',
    textClass: 'text-[color:var(--color-cc-forest)]',
  },
  to_be_demolished: {
    label: 'To be Demolished',
    dotClass: 'bg-red-500',
    textClass: 'text-red-800',
  },
  out_of_scope: {
    label: 'Out of Scope',
    dotClass: 'bg-gray-400',
    textClass: 'text-gray-600',
  },
}

export interface StatusDotLabelProps {
  status: BuildingStatus
  className?: string
}

export function StatusDotLabel({
  status,
  className,
}: StatusDotLabelProps): ReactElement {
  const presentation = PRESENTATION[status]
  return (
    <span
      className={[
        'inline-flex items-center gap-2 text-xs font-semibold',
        presentation.textClass,
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={`Status: ${presentation.label}`}
      data-status={status}
      data-testid="status-dot-label"
    >
      <span
        aria-hidden="true"
        className={[
          'inline-block h-2 w-2 rounded-full',
          presentation.dotClass,
        ].join(' ')}
      />
      {presentation.label}
    </span>
  )
}
